// Boot-time feature flags exposed by `GET /api/config/auth`. The endpoint
// surfaces flags the SPA needs BEFORE any user is authenticated (so they
// cannot live on `/api/user/whoami` — see `AuthConfigEndpoint.cs`).
//
// Shape:
//   - `loadFeatureFlags()` fetches and caches once, resolving the cached
//     snapshot. Idempotent — additional callers in the same boot cycle share
//     the same in-flight promise. Before the first load resolves the cache is
//     the safe default (`everything-off`).
//
// The module is deliberately tiny so consumers can import it without
// dragging in component-tree noise.

import { api } from './client';
import { configAuthPath } from '@voicedojo/backend-types/public';
import type { AuthConfigResponse } from '@voicedojo/backend-types/responses';

/** The boot-time flags surfaced by `GET /api/config/auth`. */
export type FeatureFlags = {
  /** `true` when `OAuth:Google:ClientId` is configured (controls the Continue-with-Google button). */
  googleAvailable: boolean;
};

/**
 * Safe pre-load default: every flag off. Used as the snapshot before the
 * first `loadFeatureFlags()` resolves. "Off" is the fail-closed posture —
 * we'd rather briefly hide an affordance than briefly show one that the
 * backend then strips at submit.
 */
const DEFAULT_FLAGS: FeatureFlags = {
  googleAvailable: false,
};

let cached: FeatureFlags = DEFAULT_FLAGS;
let inFlight: Promise<FeatureFlags> | null = null;

/**
 * Fetches the flags and memoises the result. Concurrent callers in the same
 * boot cycle share the in-flight promise so a multi-component boot only spends
 * one HTTP round-trip; the in-flight slot clears once the fetch settles, so a
 * later intentional re-sync (e.g. the root layout re-firing this when the
 * backend recovers from a boot-time outage) always hits the server afresh.
 * Errors fall back to `DEFAULT_FLAGS` (all-off) — fail-closed.
 */
export async function loadFeatureFlags(): Promise<FeatureFlags> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = (async (): Promise<FeatureFlags> => {
    // The endpoint returns PascalCase from System.Text.Json on the wire;
    // ASP.NET Core's default camel-cases public properties, so the SPA sees
    // `googleAvailable` — matches our type above.
    try {
      const data = await api.get<AuthConfigResponse>(configAuthPath(), {
        // Fail-closed boot fetch — never toast on a missing/erroring config
        // endpoint; we degrade to the all-off default instead.
        toast: false,
      });
      cached = {
        googleAvailable: data.googleAvailable === true,
      };
    } catch {
      // Fail-closed — keep the conservative default and let downstream call
      // sites render the gated affordance as hidden. Don't rethrow: a
      // missing/erroring boot endpoint shouldn't break the whole app.
      cached = DEFAULT_FLAGS;
    } finally {
      // Clear the slot so a stranded boot fetch (backend down at boot) can be
      // re-run once the backend recovers — without this the failed-and-cached
      // all-off promise would be returned forever.
      inFlight = null;
    }
    return cached;
  })();
  return inFlight;
}

/**
 * Test-only hook: clears the cached snapshot + the in-flight promise so a
 * subsequent `loadFeatureFlags()` re-runs the fetch. Not exported for app
 * code; vitest reaches it via the module import.
 */
export function __resetFeatureFlagsForTests(): void {
  cached = DEFAULT_FLAGS;
  inFlight = null;
}
